import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PushNotifications } from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import { PtyLdService } from './pty-ld.service';
import { NavController, Platform } from '@ionic/angular';
import { PtyWsService } from './pty-ws.service';
import { PtyDbService } from './pty-db.service';
import { AppVersion } from '../../appVersion'
import { TranslationService } from './translation.service';
import setLocalStorageValue from '../utils/local-storage';
import { Events } from './events.service';
import { DeepLinkService } from "./deep-link.service";
// Doesn't work with import for some reason
const moment = require('moment/min/moment-with-locales');


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  authenticationState = new BehaviorSubject(false);
  checkSessionApiVersionInterval: any;

  userData: any = null;
  apiVersion: number = 0;
  appVersion: number = 0;

  lastSecureLogoutToastTimestamp = 0;

  waitForLogoutPromise: Promise<any> = Promise.resolve();

  saveProfileWhileLoggingOut = false;

  showWarning: any;

  constructor(
    private router: Router,
    private ld: PtyLdService,
    private events: Events,
    private platform: Platform,
    private db: PtyDbService,
    private ws: PtyWsService,
    private navCtrl: NavController,
    private _translationService: TranslationService,
    private deepLinkService: DeepLinkService,
  ) {

    this.appVersion = AppVersion.appVersion;

    // assign apiVersion from local storage - set to integer since local storage is returned as string
    // localStorage value is set in APP_INITIALIZER before app.component loads
    this.apiVersion = parseInt(localStorage.getItem('api_version'));

    /* make sure appVersion and apiVersion match
       - this is called here to do a one time check from localstorage, but the version is also checked periodically
         using this.checkSessionApiVersionInterval if user is logged in  */
    // Commented out because this is done on boot elsewhere
    //this.checkApiVersion();

    // if user is stored in local storage...

    if(this.apiVersion && this.apiVersion !== 1 && localStorage.getItem('pty_user_data')) {

      /*
      - set this.userData, this.userData.token, this.userData.token_exp,
      - set the email and token inside the API service (ws) so all future calls are made with those attributes preset
      - periodically check session is valid and api version matches app version
      - trigger a change in auth state for all subscribers
      */
      this.setAuthData();
    }
  }

  checkToken() {
    return new Promise((resolve, reject) => {
      const ptyUserData = localStorage.getItem('pty_user_data');
      let dbData: any = null;
      if (ptyUserData) {
        dbData = JSON.parse(ptyUserData);
      }
      if (dbData == null) {
        this.ld.showLoading(false, {})
        this.authenticationState.next(false);
        resolve({ valid: false })
        return;
      } else {
        this.ws.invokeURL('get_current_user', {
          token: dbData.token,
          email: dbData.email,
          device_id: dbData.device_id,
          uuid: dbData.uuid,
          username: dbData.username
        }).subscribe((result: any) => {
          if (result.error == 0) {
            // reset user object in local storage from returned data
            let userData = { ...result, ...{ token: dbData.token, token_exp: dbData.token_exp } };
            localStorage.setItem('pty_user_data', JSON.stringify(userData))

            // perform setAuthData functions
            this.setAuthData();
            localStorage.setItem('lastNotificationsTime', '' + (new Date()).getTime());
            localStorage.setItem('lastCalendarTime', '' + (new Date()).getTime());

            resolve({ valid: true, vacation_uuid: result.user.vacation_uuid })
          } else {
            this.completeLogout();
            resolve({ valid: false });
          }
        }, (err: any) => {
          resolve({ valid: false });
        });
      }
    });
  }

  setAuthData() {

    // set this.userData, this.userData.token, this.userData.token_exp from local storage data
    const ptyUserData = localStorage.getItem('pty_user_data');
    let userStorage: any = {};
    if (ptyUserData) {
      userStorage = JSON.parse(ptyUserData);
    }
    if (!userStorage.user) {
      return;
    }

    this.userData = userStorage.user;
    this.userData.token = userStorage.token;
    this.userData.token_exp = userStorage.token_exp;

    // set the uuid and token inside the API service (ws) so all future calls are made with those attributes preset
    // NOTE: only token & uuid are needed right now, the rest are preserved here to prevent possible bugs for now
    this.ws.setUserInfo({
      token: userStorage.token,
      email: userStorage.user.email,
      username: userStorage.user.username,
      uuid: userStorage.user.uuid
    });

    // periodically check session is valid and api version matches app version
    this.initializeCheckSessionApiVersion();

    this.initPushNotifications();

    // trigger a change in auth state to be true for all subscribers
    this.authenticationState.next(true);
  }

  // This is necessary since the userData is cached and is used to initialize the notification counter
  // But it wasn't being updated when notifications were being read, which resulted in the counter being wrong at times
  setNotificationIsReadStatus(uuid: string, isReadStatus: boolean = true) {
    if (!this.userData || !this.userData.user_notifications || this.userData.user_notifications.length === 0) {
      return;
    }

    for (let i in this.userData.user_notifications) {
      if (this.userData.user_notifications[i].uuid === uuid) {
        this.userData.user_notifications[i].is_read = isReadStatus ? 1 : 0;
      }
    }
    this.updatePtyUserDataWithUserDataChanges();
  }

  // This function is necessary to make sure that we save all changes to the user data cache in localstorage
  // We need to update the user data cache because the initial set of notifications are stored there
  //   and the data cache is used to restore a current session
  // So if you had already viewed some notifications and refresh the window or otherwise restore a session
  //   the notification bell counter will be wrong since it's going off the cache unless you update the cache
  updatePtyUserDataWithUserDataChanges() {
    const ptyUserData = JSON.parse(localStorage.getItem('pty_user_data'));
    ptyUserData.user = this.userData;
    const ptyUserDataJson = JSON.stringify(ptyUserData);
    localStorage.setItem('pty_user_data', ptyUserDataJson);
  }

  updateNotifications(notifications: any[]) {
    if (!notifications || typeof notifications !== 'object' || !this.userData) {
      return;
    }

    this.userData.user_notifications = notifications;
    this.updatePtyUserDataWithUserDataChanges();
  }

  async logout(options={'timedOut': false}) {
    let self = this;
    //Add The event to hide the menu in this method which is called by logout by timing out and by pressing the logout btn
    self.events.publish('show-menu', false);
    this.authenticationState.next(true); // trigger an auth state change call
    this.router.navigate(['/logging-out']);
    await self.ld.showLoading(true, {fullScreen: true});

    // check to see if the logout occurred due to a timeout.  default is false
    let timedOut = options && options.timedOut ? true : false,
        userStorage:any = {user: {}};
    try {
      let storage = localStorage.getItem('pty_user_data');
      if (storage) {
        userStorage = JSON.parse(storage);
      } else {
        self.ws.clearUserInfo();
        this.completeLogout(timedOut);
        self.ld.showLoading(false);
      }
    } catch (err) {
      self.ws.clearUserInfo();
      this.completeLogout(timedOut);
      self.ld.showLoading(false);
      return;
    }

    // log the user out from the backend api
    self.ws.invokeURL('logout', {
      email: userStorage.user.email,
      token: userStorage.token,
      username: userStorage.user.username,
      uuid: userStorage.user.uuid
    }).subscribe((result: any) => {

      // clear check session interval
      clearInterval(this.checkSessionApiVersionInterval);

      self.showWarning = false;

      // remove user data from further api calls
      self.ws.clearUserInfo();

      // reset local storage and redirect to login page
      self.completeLogout(timedOut);
      self.ld.showLoading(false);
    }, (err: any) => {

      // reset local storage and redirect to login page
      self.ws.clearUserInfo();
      self.completeLogout(timedOut);
      self.ld.showLoading(false);
    });
  }


  completeLogout(timedOut=false) {
    console.log('completing logout')

    let locale = null;
    try {
      // get current local storage values we don't want to lose
      const apiVersion = localStorage.getItem('api_version');
      locale = localStorage.getItem('app_locale');
      let translations = {}, countryTranslations = {};
      const appTranslation = localStorage.getItem('app_translation');
      const rawCountryTranslation = localStorage.getItem('country_translation');
      if (appTranslation) {
        translations = JSON.parse(appTranslation);
      }
      if (rawCountryTranslation) {
        countryTranslations = JSON.parse(rawCountryTranslation);
      }

      // clear local storage
      localStorage.clear();

      // reset local storage values with old values
      localStorage.setItem('app_locale', locale);
      localStorage.setItem('api_version', apiVersion);
      localStorage.setItem('app_translation', JSON.stringify(translations));
      localStorage.setItem("country_translation", JSON.stringify(countryTranslations));
    } catch (err) {
      localStorage.clear();
    }

    this.userData = null;
    const queryParams = { timeout: timedOut ? 1 : null, lang: locale ? locale : 'en' }
    this.authenticationState.next(false);
    this.router.navigate(['/login-normal'], { queryParams });
  }

  redirectToLoginIfNotLoggedIn() {
    if (this.isAuthenticated()) {
      return;
    }
    this.router.navigate(['/login-normal']);
  }

  tokenExpirationDate() {
    const defaultValue = moment().subtract(3, 'days');
    const ptyUserData = localStorage.getItem('pty_user_data');
    if (!ptyUserData) {
      return defaultValue;
    }
    const token = JSON.parse(ptyUserData);
    if (!token || !token.token_exp) {
      return defaultValue;
    }
    return moment(token.token_exp);
  }

  getParsedSessionData(): any {
    try {
      const ptyUserData = localStorage.getItem('pty_user_data');
      if (!ptyUserData) {
        return {};
      }
      return JSON.parse(ptyUserData);
    } catch (err) {
      return {};
    }
  }

  getSecondsUntilSessionExpires() {
    const userStorage = this.getParsedSessionData();
    if (!userStorage.token_exp) {
      return -1;
    }

    return (new Date(userStorage.token_exp).getTime()) - (new Date()).getTime();
  }

  isAuthenticatedFromLocalStorage(){
    const secondsUntilSessionExpires = this.getSecondsUntilSessionExpires();
    return secondsUntilSessionExpires > 0;
  }

  isAuthenticated() {
    return this.authenticationState.value;
  }

  // check to see if appVersion and apiVersion match.  If not, redirect to bad-api-version
    checkApiVersion(){
      if (this.apiVersion !== this.appVersion){
        this.events.publish('show-menu', false);
        this.router.navigate(['/bad-api-version']);
      }
    }


  /*
    verifies that user token is valid and not expired.
     we also get the api version here from the backend to avoid multiple api calls since it needs to occur periodically
   */
  verifyTokenAndApiVersion() {
    let self = this;
    if(moment() > self.tokenExpirationDate() && !self.isAuthenticatedFromLocalStorage()) {
      return new Promise((resolve, reject) => resolve(false));
    }

    return new Promise((resolve, reject) => {

      // check if current date is greater than token expiration date
      if(moment() > self.tokenExpirationDate()) {
        resolve(false);
        return;
      } else{

        // otherwise verify token is valid on backend, return token and api version
        let userStorage = JSON.parse(localStorage.getItem('pty_user_data'));
        if (!userStorage || !userStorage.user) {
          resolve(false);
          return;
        }
        self.ws.invokeURL('verify_token_and_api', {
          token: userStorage.token,
          email: userStorage.user.email,
          device_id: userStorage.user.device_id,
          username: userStorage.user.username,
          uuid: userStorage.user.uuid
        }).subscribe((result: any) => {
          resolve(result);
        }, (err: any) => {
          resolve(false);
        });
      }
    });
  }

  isTokenStillValid() {
    let self = this;
    return new Promise((resolve, reject) => {
      let dbData = JSON.parse(localStorage.getItem('pty_user_data'));
      if (dbData === null) {
        resolve(false);
      }

      if (dbData !== null) {
        /* TODO: change this url to 'token_expired'  */
        this.ws.invokeURL('validate_token', {
          token: dbData.token,
          email: dbData.email,
          device_id: dbData.device_id,
          username: dbData.username,
          uuid: dbData.uuid
        }).subscribe((result: any) => {
          if (result.error == 0) {
            resolve(true);
          } else {
            resolve(false);
          }
        });
      }
    });
  }

  timeout() {
      this.ws.invokeURL('logout', {
        email: this.userData.email,
        token: this.userData.token,
        username: this.userData.username,
        uuid: this.userData.uuid
      }).subscribe((result: any) => {
          this.ws.clearUserInfo();
          this.completeLogout(true);
      }, (err: any) => {
          this.completeLogout(true);
      });
  }

  // check that the logged in user's session is valid and the api version is correct
    async checkSessionApiVersion(){
        if(!localStorage.getItem('pty_user_data')) return false;
        const self = this;
        const result: any = await self.verifyTokenAndApiVersion();

        // check api version
        if (result) {
            self.apiVersion = result.api_version;
            self.checkApiVersion();
        }

        // check if token is valid
        const isValid = result ? result.token_verified : false;

        // get the token expiration date
        const tokenExpirationDate = isValid ? self.tokenExpirationDate() : null;


          // if the token is valid, there is a token expiration date, showWarning is true AND
          // token expiration date is within the the next 15 minutes...

        if (isValid && tokenExpirationDate && tokenExpirationDate.isBetween(moment(), moment().add(15, 'minutes')) && self.showWarning) {
            /* warn the user that their session is about to run out */
            this.ld.showToast(`${this._translationService.translate('TimeoutWarning')} ${tokenExpirationDate.to(moment(), true)}.`);

            /* set showWarning to false so the warning is only shown 1 time */
            self.showWarning = false;
        }

      /* if the session is not valid, logout the user and remove periodic session check */
        if(!isValid /* && this.userData */ && !self.router.url.includes('login-normal')) {
            self.logout({'timedOut': true});
            clearInterval(self.checkSessionApiVersionInterval);
            return false;
        } else{
            return true;
        }
    }

    async checkForSwitchUser(switchUserToken) {
      return new Promise((resolve, reject) => {
        this.ws.invokeURL('login-switch-user', { switchUserToken }).subscribe((result: any) => {
          // On unsuccessful switch user, do nothing for now... maybe show toast message later?
          if (result.error === 1) {
            resolve(null);
          }

          let userData = { ...result.user, ...{ token: result.token, token_exp: result.token_exp } };

          let appTranslation: any = localStorage.getItem('app_translation');
          appTranslation = appTranslation ? JSON.parse(appTranslation) : {};

          // sets app locale and translation data (only needed on login.  on browser refresh, these values are set in APP_INITIALIZER)
          setLocalStorageValue('app_locale', userData.user.code_1 ? userData.user.code_1 : 'en');
          setLocalStorageValue('app_translation', JSON.stringify({...appTranslation, ...userData.user.translation}));
          setLocalStorageValue('country_translation', JSON.stringify(userData.user.countries));

          // store returned user data in local storage
          setLocalStorageValue('pty_user_data', JSON.stringify(userData));

          this.setAuthData();

          //Once the login process is finished the templates are called to save in local storage
          this.loadUserTemplates(this.reloadScreen);

        }, (err: any) => {
          resolve(null);
        });

      });
    }


    // periodically check that the logged in user's session is valid and the api version is correct
    initializeCheckSessionApiVersion(){
        this.checkSessionApiVersionInterval = setInterval(async()=>{
            await this.checkSessionApiVersion();
        }, 180000)
    }

    showSecureLogoutToast() {
      if (this.lastSecureLogoutToastTimestamp > 0) {
        const currentTime = (new Date()).getTime();
        if (currentTime - this.lastSecureLogoutToastTimestamp < 300000) {
          this.lastSecureLogoutToastTimestamp = (new Date()).getTime();
          return;
        }
      }
      this.lastSecureLogoutToastTimestamp = (new Date()).getTime();

      this.ld.showToast(this._translationService.translate('SecureTimeout'));
    }

    async initPushNotifications() {
      const info = await Device.getInfo();

      // Only initialize push notifications for native app users
      if (info.platform === 'ios' || info.platform === 'android') {
        //await this.addPushNotificationListeners(info.platform);
        await this.registerPushNotifications();
        await this.getDeliveredNotifications();
      }
    }

    async registerPushNotifications() {
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.log('User denied permissions!');
        return;
      }

      await PushNotifications.register();
    }

    async getDeliveredNotifications() {
      const notificationList = await PushNotifications.getDeliveredNotifications();
      console.log('delivered notifications', notificationList);
    }

    loadUserTemplates(myCallback) {
      let self = this;
  
      self.ws.invokeURL('user_templates', {}).subscribe((result: any) => {
        if (result.error === 0) {
          let myTemplates = [];
          let adminTemplates = [];
  
          //Divide the temples in 2 categories
          result.templates.forEach((template) => {
            if (template.uploaded_by !== 'A') {
              myTemplates.push(template);
            } else {
              adminTemplates.push(template);
            }
          });
  
          //Load user templates in local storage when user logs in
          localStorage.setItem('myTemplatesInstorage', JSON.stringify(myTemplates.sort((a, b) => a.template_order - b.template_order)));
          localStorage.setItem('adminTemplatesInstorage', JSON.stringify(adminTemplates.sort((a, b) => a.template_order - b.template_order)));
        }

        myCallback();

      });
    }
  

    reloadScreen() {
      // Reboot to finish login
      location.href = location.href.replace(location.search, '');
    }
  
    addToWaitForLogoutPromiseChain(newPromise: Promise<any>) {
      this.waitForLogoutPromise = Promise.all([this.waitForLogoutPromise, newPromise]);
    }

    getWaitForLogoutPromiseChain(): Promise<any> {
      return this.waitForLogoutPromise;
    }
}
